import { Remove } from "@mui/icons-material";

import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { addImages, removeImages } from "../../../redux/Catalog/catalogActions";
import toast from "react-hot-toast";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

const SelectVariants = ({ formulations, selected, variants, setSelected }) => {
  const dispatch = useDispatch();
  const colorSelected = !!(
    typeof variants === "object" && variants?.colour?.custom
  );

  const isSameVariant = (a, b) => {
    if (colorSelected && a.color !== b.color) return false;
    for (const key of customVariantKeys) {
      if (a[key] !== b[key]) return false;
    }
    return a.type === b.type && a.value === b.value && a.unit === b.unit;
  };

  const [uploadingVariants, setUploadingVariants] = useState(new Set());
  const [deletingVariants, setDeletingVariants] = useState(new Set());

  const customVariantKeys = Array.from(
    new Set(
      Object.keys(variants).filter(
        (key) =>
          key !== "colour" &&
          key !== "images" &&
          variants[key] &&
          variants[key].custom &&
          Array.isArray(variants[key].values) &&
          variants[key].values.length > 0
      )
    )
  );

  function cartesianProduct(arrays) {
    return arrays.reduce(
      (a, b) =>
        a
          .map((x) => b.map((y) => x.concat([y])))
          .reduce((a, b) => a.concat(b), []),
      [[]]
    );
  }

  function dedupeAndAutocomplete(values) {
    const seen = new Set();
    return values
      .filter((v) => v != null && String(v).trim().length > 0)
      .map((v) => String(v).trim())
      .reduce((acc, curr) => {
        // If a longer value starts with a shorter one, replace the shorter
        const existing = Array.from(seen).find(
          (s) => curr.startsWith(s) && curr.length > s.length
        );
        if (existing) {
          seen.delete(existing);
          seen.add(curr);
          return acc.map((x) => (x === existing ? curr : x));
        }
        if (!seen.has(curr)) {
          seen.add(curr);
          acc.push(curr);
        }
        return acc;
      }, []);
  }

  // Generate all possible variants (with color and any custom variants if enabled)
  let allDisplayVariants = [];
  if (colorSelected || customVariantKeys.length > 0) {
    // Get all non-empty color values
    // Only use fully filled values for color and custom variants
    const colorValues = colorSelected
      ? dedupeAndAutocomplete(variants.colour?.values || [])
      : [null];
    const customValuesArr = customVariantKeys.map((key) =>
      dedupeAndAutocomplete(variants[key]?.values || [])
    );
    const customValuesProduct =
      customValuesArr.length > 0 ? cartesianProduct(customValuesArr) : [[null]];

    allDisplayVariants = formulations.flatMap((formulation) =>
      formulation.values.flatMap((val) =>
        colorValues.flatMap((color) =>
          customValuesProduct.map((customVals) => {
            // Build the variant object
            const variant = {
              type: formulation.type,
              value: val.value,
              unit: val.unit,
            };
            if (color) variant.color = color;
            // Add each custom variant key/value
            customVariantKeys.forEach((key, idx) => {
              variant[key] = customVals[idx];
            });
            return variant;
          })
        )
      )
    );
  } else {
    allDisplayVariants = formulations.flatMap((f) =>
      f.values.map((v) => ({ type: f.type, value: v.value, unit: v.unit }))
    );
  }

  // After generating allDisplayVariants, filter to only include fully filled variants
  allDisplayVariants = allDisplayVariants.filter((variant) => {
    if (colorSelected && !variant.color) return false;
    for (const key of customVariantKeys) {
      if (!variant[key]) return false;
    }
    return true;
  });

  const allSelected =
    allDisplayVariants.length > 0 &&
    allDisplayVariants.every((variant) =>
      selected.some((sel) => isSameVariant(sel, variant))
    );

  const handleSelect = (variant) => {
    const isSelected = selected.some((sel) => isSameVariant(sel, variant));
    if (isSelected) {
      setSelected(selected.filter((sel) => !isSameVariant(sel, variant)));
    } else {
      setSelected([...selected, variant]);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(allDisplayVariants);
    }
  };
  const SelectAllRow = ({ allSelected, handleSelectAll, selectedCount }) => (
    <div className="flex justify-between items-center p-3 border-b border-b-[#E1E6E1]">
      <div className="flex gap-2 items-center">
        {!allSelected ? (
          <button
            type="button"
            aria-label="Select All Variants"
            className="w-[18px] h-[18px] flex items-center justify-center rounded bg-white border-2 border-[#96A397] outline-none "
            onClick={handleSelectAll}
            tabIndex={0}
          >
            <Remove fontSize="small" sx={{ color: "#FFF", fontSize: 18 }} />
          </button>
        ) : (
          <button
            type="button"
            aria-label="Select All Variants"
            className="w-[18px] h-[18px] flex items-center justify-center rounded bg-[#2B8C34] border border-[#2B8C34] outline-none focus:ring-2 focus:ring-[#2B8C34]"
            onClick={handleSelectAll}
            tabIndex={0}
          >
            <Remove fontSize="small" sx={{ color: "#FFF", fontSize: 18 }} />
          </button>
        )}

        <p className="text-[#5F6D60] font-semibold text-sm">Select All</p>
      </div>
      <div>
        <p className="text-primary font-semibold text-sm">
          {selectedCount} Selected
        </p>
      </div>
    </div>
  );

  console.log(selected, "selected variants");

  return (
    <div>
      {formulations[0].type.length !== 0 && (
        <div className="rounded-lg bg-white h-fit border border-[#E1E6E1] mt-3">
          {/* Header */}
          <div className="flex py-3 border-b justify-between px-3 items-center border-b-[#E1E6E1] gap-[10px]">
            <div className="flex flex-col gap-1">
              <p className="text-[#2E332F] font-semibold text-sm leading-[18px]">
                Variant Options
              </p>
              <p className="text-[#96A397] font-medium text-sm leading-[18px]">
                Choose and select the options that apply to the product
              </p>
            </div>
          </div>
          <>
            <SelectAllRow
              handleSelectAll={handleSelectAll}
              selectedCount={selected.length}
              allSelected={allSelected}
            />
            {allDisplayVariants.map((variant, idx) => (
              <div
                key={
                  customVariantKeys.map((key) => variant[key]).join("-") +
                  "-" +
                  variant.type +
                  "-" +
                  variant.value +
                  "-" +
                  variant.unit +
                  (variant.color ? "-" + variant.color : "")
                }
                className="flex justify-between items-center p-3 border-b border-b-[#E1E6E1]"
              >
                <div className="flex justify-between items-center w-full">
                  <div className="flex gap-2 items-center ">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-primary accent-primary border-[#E1E6E1] rounded focus:ring-[#2B8C34] cursor-pointer"
                      checked={selected.some((sel) =>
                        isSameVariant(sel, variant)
                      )}
                      onChange={() => handleSelect(variant)}
                      aria-label={`Select variant ${variant.type} ${
                        variant.value
                      } ${variant.unit}${
                        variant.color ? ` ${variant.color}` : ""
                      }${customVariantKeys.map((key) =>
                        variant[key] ? ` ${variant[key]}` : ""
                      )}`}
                    />
                    {/* -------IMAGES AND VARIANTS---- */}
                    <div
                      className={`${(() => {
                        const selectedVariant = selected.find((sel) =>
                          isSameVariant(sel, variant)
                        );
                        const images = selectedVariant?.images || [];
                        return images.length <= 1 ? "flex-row" : "flex-col";
                      })()} flex gap-2`}
                    >
                      {/* Image preview row for selected variant */}
                      {(() => {
                        const selectedVariant = selected.find((sel) =>
                          isSameVariant(sel, variant)
                        );

                        const hasImages =
                          selectedVariant &&
                          selectedVariant.images &&
                          selectedVariant.images.length > 0;

                        if (hasImages) {
                          const images = selectedVariant.images;

                          return (
                            <div className="ml-2 flex gap-1">
                              {images?.map((imageUrl, index) => (
                                <div key={index} className="relative group">
                                  <Zoom
                                    image={{
                                      src: imageUrl,
                                    }}
                                  >
                                    <div className="w-8 h-8 flex items-center justify-center border border-[#E1E6E1] rounded overflow-hidden bg-[#F8F9F8]">
                                      <img
                                        src={imageUrl}
                                        alt={`variant preview ${index + 1}`}
                                        className="object-cover w-full h-full"
                                      />
                                    </div>
                                  </Zoom>
                                  {/* Cancel icon on hover */}
                                  <button
                                    type="button"
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();

                                      // Create variant key for tracking deletion state
                                      const variantKey =
                                        customVariantKeys
                                          .map((key) => variant[key])
                                          .join("-") +
                                        "-" +
                                        variant.type +
                                        "-" +
                                        variant.value +
                                        "-" +
                                        variant.unit +
                                        (variant.color
                                          ? "-" + variant.color
                                          : "");

                                      // Set deleting state
                                      setDeletingVariants(
                                        (prev) => new Set([...prev, variantKey])
                                      );

                                      try {
                                        // Remove image from Cloudinary
                                        await dispatch(
                                          removeImages({ image_url: imageUrl })
                                        );

                                        // Update selected variants to remove this specific image
                                        setSelected((prevSelected) =>
                                          prevSelected.map((sel) => {
                                            if (isSameVariant(sel, variant)) {
                                              if (
                                                sel.images &&
                                                sel.images.length > 1
                                              ) {
                                                // Remove specific image from array
                                                const updatedImages =
                                                  sel.images.filter(
                                                    (img) => img !== imageUrl
                                                  );
                                                return {
                                                  ...sel,
                                                  images: updatedImages,
                                                };
                                              } else {
                                                // Remove images property if this was the last image
                                                const { images, ...rest } = sel;
                                                return rest;
                                              }
                                            }
                                            return sel;
                                          })
                                        );

                                        toast.success(
                                          "Image removed successfully!"
                                        );
                                      } catch (error) {
                                        console.error("Remove error:", error);
                                        toast.error("Failed to remove image");
                                      } finally {
                                        // Remove deleting state
                                        setDeletingVariants((prev) => {
                                          const newSet = new Set(prev);
                                          newSet.delete(variantKey);
                                          return newSet;
                                        });
                                      }
                                    }}
                                  >
                                    <svg
                                      width="8"
                                      height="8"
                                      viewBox="0 0 8 8"
                                      fill="none"
                                    >
                                      <path
                                        d="M6.5 1.5L1.5 6.5M1.5 1.5L6.5 6.5"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          );
                        } else {
                          return (
                            <div className="ml-2 w-8 h-8 flex items-center justify-center border border-[#E1E6E1] rounded overflow-hidden bg-[#F8F9F8]">
                              <div className="flex flex-col items-center justify-center text-[#B0B7AE]">
                                <svg
                                  width="25"
                                  height="25"
                                  fill="none"
                                  viewBox="0 0 20 20"
                                >
                                  <rect
                                    width="20"
                                    height="20"
                                    rx="4"
                                    fill="#F0F2F0"
                                  />
                                  <path
                                    d="M6 13l2.5-3 2.5 3 3-4 3.5 5H3l3-4z"
                                    fill="#B0B7AE"
                                  />
                                  <circle cx="7" cy="8" r="1" fill="#B0B7AE" />
                                </svg>
                              </div>
                            </div>
                          );
                        }
                      })()}
                      <div className="flex gap-1 items-center">
                        <div className="border border-[#E1E6E1] bg-white px-1 py-[2px] rounded-[4px]">
                          <p className="text-[#5F6D60] font-semibold text-sm">
                            {variant.type}
                          </p>
                        </div>
                        <p className="text-[#5F6D60] font-semibold text-sm">
                          /
                        </p>
                        <div className="border border-[#E1E6E1] bg-white px-1 py-[2px] rounded-[4px]">
                          <p className="text-[#5F6D60] font-semibold text-sm">
                            {variant.value} {variant.unit}
                          </p>
                        </div>

                        {colorSelected && (
                          <>
                            <p className="text-[#5F6D60] font-semibold text-sm">
                              /
                            </p>
                            <div className="border border-[#E1E6E1] bg-white px-1 py-[2px] rounded-[4px]">
                              <p className="text-[#5F6D60] font-semibold text-sm">
                                {variant.color}
                              </p>
                            </div>
                          </>
                        )}
                        {customVariantKeys.map((key) =>
                          variant[key] ? (
                            <>
                              <p className="text-[#5F6D60] font-semibold text-sm">
                                /
                              </p>
                              <div className="border border-[#E1E6E1] bg-white px-1 py-[2px] rounded-[4px]">
                                <p className="text-[#5F6D60] font-semibold text-sm">
                                  {variant[key]}
                                </p>
                              </div>
                            </>
                          ) : null
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Add Image button for selected variants */}
                  {selected.some((sel) => isSameVariant(sel, variant)) && (
                    <div className="ml-4 flex gap-2">
                      {(() => {
                        const variantKey =
                          customVariantKeys
                            .map((key) => variant[key])
                            .join("-") +
                          "-" +
                          variant.type +
                          "-" +
                          variant.value +
                          "-" +
                          variant.unit +
                          (variant.color ? "-" + variant.color : "");

                        const isUploading = uploadingVariants.has(variantKey);
                        const isDeleting = deletingVariants.has(variantKey);
                        const isDisabled = isUploading || isDeleting;

                        return (
                          <label
                            className={`inline-flex items-center ${
                              isDisabled
                                ? "cursor-not-allowed opacity-60"
                                : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              disabled={isDisabled}
                              style={{ display: "none" }}
                              onChange={async (e) => {
                                if (
                                  e.target.files &&
                                  e.target.files.length > 0 &&
                                  !isDisabled
                                ) {
                                  const files = Array.from(e.target.files);

                                  // Set loading state
                                  setUploadingVariants(
                                    (prev) => new Set([...prev, variantKey])
                                  );

                                  // Create FormData for upload
                                  const formData = new FormData();
                                  files.forEach((file) => {
                                    formData.append("images", file);
                                  });

                                  try {
                                    // Upload images to get Cloudinary URLs
                                    const response = await dispatch(
                                      addImages({ data: formData })
                                    );

                                    if (
                                      response.payload &&
                                      response.payload.data &&
                                      response.payload.data.images
                                    ) {
                                      const uploadedImages =
                                        response.payload.data.images;

                                      // Store Cloudinary URLs in selected variants
                                      setSelected((prevSelected) =>
                                        prevSelected.map((sel) => {
                                          if (isSameVariant(sel, variant)) {
                                            // Get existing images and append new ones
                                            const existingImages =
                                              sel.images || [];
                                            const allImages = [
                                              ...existingImages,
                                              ...uploadedImages,
                                            ];

                                            return {
                                              ...sel,
                                              images: allImages, // Append to existing images
                                            };
                                          }
                                          return sel;
                                        })
                                      );

                                      toast.success(
                                        `${uploadedImages.length} image(s) uploaded successfully!`
                                      );
                                    } else {
                                      toast.error(
                                        "Failed to upload images - Invalid response format"
                                      );
                                      console.error(
                                        "Invalid response:",
                                        response.payload
                                      );
                                    }
                                  } catch (error) {
                                    console.error("Upload error:", error);
                                    toast.error("Failed to upload images");
                                  } finally {
                                    // Remove loading state
                                    setUploadingVariants((prev) => {
                                      const newSet = new Set(prev);
                                      newSet.delete(variantKey);
                                      return newSet;
                                    });
                                  }
                                }
                              }}
                            />
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                isDisabled
                                  ? "bg-gray-300 text-gray-500"
                                  : "bg-[#E1E6E1] text-[#2E332F] hover:bg-[#d1d5db]"
                              }`}
                            >
                              {isUploading
                                ? "Uploading..."
                                : isDeleting
                                ? "Deleting Image..."
                                : "Add Images"}
                            </span>
                          </label>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </>
        </div>
      )}
    </div>
  );
};

export default SelectVariants;
